import { useMemo, useState, type ReactElement } from 'react'
import PersonaAvatar from './PersonaAvatar'
import type { ProjectTask } from '../lib/types'

const STATUS_COLORS: Record<string, string> = {
  pending: '#6b7280', in_progress: '#3b82f6', completed: '#3fb950',
  blocked: '#f85149', deferred: '#8b5cf6', review: '#d29922',
}

const STATUS_BG: Record<string, string> = {
  pending: 'rgba(107,114,128,0.2)', in_progress: 'rgba(59,130,246,0.15)',
  completed: 'rgba(63,185,80,0.12)', blocked: 'rgba(248,81,73,0.15)',
  deferred: 'rgba(139,92,246,0.15)', review: 'rgba(210,153,34,0.15)',
}

interface LayoutRow {
  task: ProjectTask
  lane: number
  col: number   // column in the dependency graph (depth)
  maxCol: number
}

// Topological sort + lane assignment using dependency graph
function computeLayout(tasks: ProjectTask[]): LayoutRow[] {
  const taskMap = new Map(tasks.map(t => [t.id, t]))
  const depthMap = new Map<string, number>()

  // Compute depth (column) for each task based on dependency chain
  function getDepth(taskId: string, visited = new Set<string>()): number {
    if (depthMap.has(taskId)) return depthMap.get(taskId)!
    if (visited.has(taskId)) return 0 // cycle guard
    visited.add(taskId)

    const task = taskMap.get(taskId)
    if (!task?.depends_on?.length) {
      depthMap.set(taskId, 0)
      return 0
    }

    const maxParentDepth = Math.max(
      ...task.depends_on
        .filter(d => taskMap.has(d))
        .map(d => getDepth(d, visited))
    )
    const depth = maxParentDepth + 1
    depthMap.set(taskId, depth)
    return depth
  }

  tasks.forEach(t => getDepth(t.id))

  // Group by depth column
  const byCol = new Map<number, ProjectTask[]>()
  tasks.forEach(t => {
    const col = depthMap.get(t.id) || 0
    if (!byCol.has(col)) byCol.set(col, [])
    byCol.get(col)!.push(t)
  })

  const maxCol = Math.max(0, ...Array.from(byCol.keys()))

  // Assign lanes (vertical position) — tasks at same depth get sequential lanes
  // Sort within column by order field, then by task id
  const rows: LayoutRow[] = []
  let globalLane = 0
  const taskLane = new Map<string, number>()

  // Process columns left to right
  for (let col = 0; col <= maxCol; col++) {
    const colTasks = byCol.get(col) || []
    // Sort: try to place tasks near their dependencies
    colTasks.sort((a, b) => {
      // If task has a dependency, position near it
      const aParentLane = a.depends_on?.length
        ? Math.min(...a.depends_on.filter(d => taskLane.has(d)).map(d => taskLane.get(d)!))
        : Infinity
      const bParentLane = b.depends_on?.length
        ? Math.min(...b.depends_on.filter(d => taskLane.has(d)).map(d => taskLane.get(d)!))
        : Infinity
      if (aParentLane !== bParentLane) return aParentLane - bParentLane
      return (a.order || 0) - (b.order || 0)
    })

    for (const task of colTasks) {
      taskLane.set(task.id, globalLane)
      rows.push({ task, lane: globalLane, col, maxCol })
      globalLane++
    }
  }

  return rows
}

// Draw dependency arrows as SVG paths
function DependencyArrows({
  rows,
  barX,
  barWidth,
  rowY,
  rowHeight,
}: {
  rows: LayoutRow[]
  barX: (col: number) => number
  barWidth: number
  rowY: (lane: number) => number
  rowHeight: number
}) {
  const taskLane = new Map(rows.map(r => [r.task.id, r.lane]))
  const taskCol = new Map(rows.map(r => [r.task.id, r.col]))
  const paths: ReactElement[] = []

  rows.forEach(row => {
    const deps = row.task.depends_on || []
    deps.forEach(depId => {
      if (!taskLane.has(depId)) return
      const fromLane = taskLane.get(depId)!
      const fromCol = taskCol.get(depId)!
      const toLane = row.lane
      const toCol = row.col

      const x1 = barX(fromCol) + barWidth
      const y1 = rowY(fromLane) + rowHeight / 2
      const x2 = barX(toCol)
      const y2 = rowY(toLane) + rowHeight / 2

      const midX = (x1 + x2) / 2

      paths.push(
        <g key={`${depId}-${row.task.id}`}>
          <path
            d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`}
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth={1.5}
            strokeDasharray={row.task.status === 'blocked' ? '4,3' : 'none'}
            opacity={0.5}
          />
          {/* Arrow head */}
          <polygon
            points={`${x2},${y2} ${x2 - 6},${y2 - 3.5} ${x2 - 6},${y2 + 3.5}`}
            fill="var(--text-muted)"
            opacity={0.5}
          />
        </g>
      )
    })
  })

  return <>{paths}</>
}

export default function GanttTimeline({
  tasks,
  projectId,
}: {
  tasks: ProjectTask[]
  projectId: string
}) {
  const [hoveredTask, setHoveredTask] = useState<string | null>(null)

  const rows = useMemo(() => computeLayout(tasks), [tasks])

  if (tasks.length === 0) {
    return (
      <p className="text-xs py-4" style={{ color: 'var(--text-muted)' }}>
        No tasks to visualize
      </p>
    )
  }

  // Layout constants
  const labelWidth = 200
  const colGap = 40     // gap between columns for arrows
  const barWidth = 160
  const rowHeight = 44
  const topPadding = 40
  const leftPadding = 16
  const rightPadding = 32

  const maxCol = Math.max(0, ...rows.map(r => r.col))
  const totalWidth = labelWidth + leftPadding + (maxCol + 1) * (barWidth + colGap) + rightPadding
  const totalHeight = topPadding + rows.length * rowHeight + 16

  const barX = (col: number) => labelWidth + leftPadding + col * (barWidth + colGap)
  const rowYFn = (lane: number) => topPadding + lane * rowHeight

  // Column headers
  const colHeaders = Array.from({ length: maxCol + 1 }, (_, i) => {
    const tasksInCol = rows.filter(r => r.col === i)
    const hasBlocked = tasksInCol.some(r => r.task.status === 'blocked')
    const allComplete = tasksInCol.every(r => r.task.status === 'completed')
    return { col: i, hasBlocked, allComplete }
  })

  // Summary stats
  const completed = tasks.filter(t => t.status === 'completed').length
  const inProgress = tasks.filter(t => t.status === 'in_progress').length
  const blocked = tasks.filter(t => t.status === 'blocked').length
  const pending = tasks.filter(t => t.status === 'pending').length

  return (
    <div>
      {/* Stats bar */}
      <div className="flex items-center gap-4 mb-3 px-1">
        {completed > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS.completed }} />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{completed} done</span>
          </div>
        )}
        {inProgress > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS.in_progress }} />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{inProgress} active</span>
          </div>
        )}
        {blocked > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS.blocked }} />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{blocked} blocked</span>
          </div>
        )}
        {pending > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS.pending }} />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{pending} pending</span>
          </div>
        )}
      </div>

      {/* Scrollable timeline */}
      <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--border)', background: 'var(--bg-body)' }}>
        <svg
          width={Math.max(totalWidth, 600)}
          height={totalHeight}
          viewBox={`0 0 ${Math.max(totalWidth, 600)} ${totalHeight}`}
          style={{ display: 'block' }}
        >
          {/* Column phase labels */}
          {colHeaders.map(({ col, allComplete }) => (
            <text
              key={col}
              x={barX(col) + barWidth / 2}
              y={20}
              textAnchor="middle"
              fontSize={11}
              fill={allComplete ? 'var(--success)' : 'var(--text-muted)'}
              fontWeight={500}
            >
              {maxCol === 0 ? 'Tasks' : `Phase ${col + 1}`}
            </text>
          ))}

          {/* Column separator lines */}
          {colHeaders.slice(1).map(({ col }) => (
            <line
              key={`sep-${col}`}
              x1={barX(col) - colGap / 2}
              y1={topPadding - 8}
              x2={barX(col) - colGap / 2}
              y2={totalHeight - 8}
              stroke="var(--border)"
              strokeWidth={1}
              strokeDasharray="4,4"
              opacity={0.5}
            />
          ))}

          {/* Row backgrounds (alternating) */}
          {rows.map((row, i) => (
            <rect
              key={`bg-${row.task.id}`}
              x={0}
              y={rowYFn(row.lane)}
              width={Math.max(totalWidth, 600)}
              height={rowHeight}
              fill={i % 2 === 0 ? 'transparent' : 'var(--bg-surface)'}
              opacity={0.3}
            />
          ))}

          {/* Dependency arrows (behind bars) */}
          <DependencyArrows
            rows={rows}
            barX={barX}
            barWidth={barWidth}
            rowY={rowYFn}
            rowHeight={rowHeight}
          />

          {/* Task labels (left column) */}
          {rows.map(row => {
            const y = rowYFn(row.lane)
            const isHovered = hoveredTask === row.task.id
            return (
              <g key={`label-${row.task.id}`}>
                <text
                  x={12}
                  y={y + rowHeight / 2 + 1}
                  dominantBaseline="middle"
                  fontSize={12}
                  fill={isHovered ? 'var(--accent)' : 'var(--text-primary)'}
                  fontWeight={isHovered ? 600 : 400}
                  style={{ cursor: 'pointer' }}
                >
                  {row.task.title.length > 24
                    ? row.task.title.slice(0, 22) + '…'
                    : row.task.title}
                </text>
              </g>
            )
          })}

          {/* Task bars */}
          {rows.map(row => {
            const x = barX(row.col)
            const y = rowYFn(row.lane) + 6
            const h = rowHeight - 12
            const status = row.task.status
            const isHovered = hoveredTask === row.task.id
            const barColor = STATUS_COLORS[status] || '#6b7280'
            const bgColor = STATUS_BG[status] || 'rgba(107,114,128,0.2)'

            return (
              <g key={`bar-${row.task.id}`}>
                {/* Background bar */}
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={h}
                  rx={6}
                  fill={bgColor}
                  stroke={isHovered ? barColor : 'transparent'}
                  strokeWidth={isHovered ? 1.5 : 0}
                  style={{ cursor: 'pointer', transition: 'stroke 0.15s' }}
                  onMouseEnter={() => setHoveredTask(row.task.id)}
                  onMouseLeave={() => setHoveredTask(null)}
                />

                {/* Progress fill for completed/in_progress */}
                {(status === 'completed' || status === 'in_progress') && (
                  <rect
                    x={x}
                    y={y}
                    width={status === 'completed' ? barWidth : barWidth * 0.5}
                    height={h}
                    rx={6}
                    fill={barColor}
                    opacity={status === 'completed' ? 0.25 : 0.15}
                    style={{ pointerEvents: 'none' }}
                  />
                )}

                {/* Left accent stripe */}
                <rect
                  x={x}
                  y={y}
                  width={4}
                  height={h}
                  rx={2}
                  fill={barColor}
                  style={{ pointerEvents: 'none' }}
                />

                {/* Status icon */}
                {status === 'completed' && (
                  <text
                    x={x + 12}
                    y={y + h / 2 + 1}
                    dominantBaseline="middle"
                    fontSize={12}
                    fill={barColor}
                    style={{ pointerEvents: 'none' }}
                  >
                    ✓
                  </text>
                )}
                {status === 'blocked' && (
                  <text
                    x={x + 12}
                    y={y + h / 2 + 1}
                    dominantBaseline="middle"
                    fontSize={11}
                    fill={barColor}
                    style={{ pointerEvents: 'none' }}
                  >
                    ⊘
                  </text>
                )}

                {/* Task ID */}
                <text
                  x={x + (status === 'completed' || status === 'blocked' ? 26 : 12)}
                  y={y + h / 2 + 1}
                  dominantBaseline="middle"
                  fontSize={11}
                  fill="var(--text-secondary)"
                  fontWeight={500}
                  style={{ pointerEvents: 'none' }}
                >
                  {row.task.id.replace('task-', '#')}
                </text>

                {/* Status label */}
                <text
                  x={x + barWidth - (row.task.assignee ? 30 : 8)}
                  y={y + h / 2 + 1}
                  dominantBaseline="middle"
                  textAnchor="end"
                  fontSize={10}
                  fill={barColor}
                  fontWeight={500}
                  style={{ pointerEvents: 'none', textTransform: 'capitalize' }}
                >
                  {status.replace('_', ' ')}
                </text>

                {/* Clickable overlay for navigation */}
                <a href={`/project/${projectId}/task/${row.task.id}`}>
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={h}
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredTask(row.task.id)}
                    onMouseLeave={() => setHoveredTask(null)}
                  />
                </a>
              </g>
            )
          })}

          {/* Assignee avatars (rendered as foreignObject for React components) */}
          {rows.map(row => {
            if (!row.task.assignee) return null
            const x = barX(row.col) + barWidth - 24
            const y = rowYFn(row.lane) + (rowHeight - 20) / 2
            return (
              <foreignObject key={`avatar-${row.task.id}`} x={x} y={y} width={20} height={20}>
                <PersonaAvatar name={row.task.assignee} size={20} />
              </foreignObject>
            )
          })}
        </svg>
      </div>

      {/* Hover tooltip */}
      {hoveredTask && (
        <div className="mt-2 px-3 py-2 rounded-md text-xs" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
          {(() => {
            const task = tasks.find(t => t.id === hoveredTask)
            if (!task) return null
            return (
              <div className="flex items-center gap-3">
                <span style={{ color: 'var(--text-primary)' }} className="font-medium">{task.title}</span>
                {task.assignee && (
                  <span style={{ color: 'var(--text-muted)' }}>→ {task.assignee}</span>
                )}
                {task.depends_on && task.depends_on.length > 0 && (
                  <span style={{ color: 'var(--text-muted)' }}>
                    depends on: {task.depends_on.join(', ')}
                  </span>
                )}
                {task.blocks && task.blocks.length > 0 && (
                  <span style={{ color: 'var(--text-muted)' }}>
                    blocks: {task.blocks.join(', ')}
                  </span>
                )}
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}
